/**
 * LiveUsersWidget — admin-only pill that polls /api/admin/live-users
 * every 30 seconds and shows a 🟢 indicator with the current count of
 * users active in the last 2 minutes.
 *
 * Click the pill to open a popover listing each active user (email,
 * name, last-seen time). Auto-pauses polling when the tab is hidden
 * to avoid wasting cycles on backgrounded admin tabs.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Users } from 'lucide-react';
import { apiFetch } from '../App';

export default function LiveUsersWidget() {
  const [data, setData] = useState({ count: 0, users: [], as_of: null });
  const [stats, setStats] = useState(null); // { today, this_month, total }
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);

  const refresh = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    setLoading(true);
    try {
      const [live, visitors] = await Promise.all([
        apiFetch('/admin/live-users'),
        apiFetch('/admin/visitor-stats').catch(() => ({ data: null })),
      ]);
      setData(live.data);
      if (visitors.data) setStats(visitors.data);
    } catch {
      // Non-fatal — keep the previous count and try again on the next tick.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  // Click-outside to close popover
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const formatRelative = (iso) => {
    if (!iso) return '?';
    try {
      const t = new Date(iso).getTime();
      const seconds = Math.max(0, Math.round((Date.now() - t) / 1000));
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.round(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.round(minutes / 60);
      return `${hours}h ago`;
    } catch {
      return '?';
    }
  };

  return (
    <div className="relative inline-block" ref={popRef} data-testid="live-users-widget">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-700 text-sm transition"
        title={`${data.count} user${data.count === 1 ? '' : 's'} active in the last 2 min${stats ? ` · ${stats.today} unique visitor${stats.today === 1 ? '' : 's'} today` : ''}`}
        data-testid="live-users-button"
      >
        <span
          className={`w-2 h-2 rounded-full ${data.count > 0 ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-500'}`}
        />
        <Users className="h-3.5 w-3.5 text-zinc-300" />
        <span className="text-white font-medium" data-testid="live-users-count">
          {loading && data.count === 0 ? '…' : data.count}
        </span>
        <span className="text-zinc-400 text-xs">online</span>
        {stats && (
          <span
            className="ml-1 pl-2 border-l border-zinc-700 text-[11px] text-emerald-300"
            data-testid="visitor-stats-today-pill"
            title="Unique visitors today"
          >
            {stats.today} today
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50 overflow-hidden"
          data-testid="live-users-popover"
        >
          {/* Visitor stats — sits above the "active users" list so admins get
              a quick pulse of traffic before drilling into the roster. */}
          {stats && (
            <div className="px-3 py-2 border-b border-zinc-800 bg-zinc-950/50" data-testid="visitor-stats">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Unique visitors</div>
              <div className="flex items-baseline justify-between gap-2 text-white">
                <div className="text-center flex-1" data-testid="visitor-stats-today">
                  <div className="text-lg font-bold text-emerald-400">{stats.today}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wide">Today</div>
                </div>
                <div className="text-center flex-1" data-testid="visitor-stats-month">
                  <div className="text-lg font-bold text-sky-400">{stats.this_month}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wide">Month</div>
                </div>
                <div className="text-center flex-1" data-testid="visitor-stats-total">
                  <div className="text-lg font-bold text-white">{stats.total}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wide">Total</div>
                </div>
              </div>
            </div>
          )}
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <div className="text-xs font-medium text-zinc-300">
              Active in last {Math.round((data.window_seconds || 120) / 60)} min
            </div>
            <button
              onClick={refresh}
              className="text-xs text-zinc-400 hover:text-white"
              data-testid="live-users-refresh"
            >
              Refresh
            </button>
          </div>
          {data.users.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-zinc-500">
              No one else online right now.
            </div>
          ) : (
            <ul className="max-h-72 overflow-y-auto divide-y divide-zinc-800">
              {data.users.map((u) => (
                <li key={u.user_id} className="px-3 py-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-medium truncate">
                        {u.name || u.email || u.user_id}
                      </div>
                      {u.name && u.email && (
                        <div className="text-zinc-500 truncate">{u.email}</div>
                      )}
                    </div>
                    <div className="text-zinc-400 shrink-0">{formatRelative(u.last_seen_at)}</div>
                  </div>
                  {u.role && u.role !== 'user' && (
                    <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-purple-500/20 text-purple-300">
                      {u.role}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
